import React, { useState, useRef, useEffect } from "react";
import Header from "../components/Header";
import { stockService, distributorService } from "../services/apiService";
import { auditService } from "../services/auditService";
import { useLoader } from "../utils/LoaderContext";
import DistributorBillPopup from "../components/DistributorBillPopup";
import Toast from "../components/Toast";
import BulkStockUploadModal from "../components/BulkStockUploadModal";

// ─── Reusable input class ─────────────────────────────────────────────────────
const cls = (err) =>
  `w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 focus:outline-none ${
    err ? "border-red-400 bg-red-50" : "border-blue-300"
  }`;

// ─── Modal wrapper ────────────────────────────────────────────────────────────
const Modal = ({ children }) => (
  <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl border border-blue-200">
      {children}
    </div>
  </div>
);

// ─── Confirm popup (Yes / Cancel) ─────────────────────────────────────────────
const ConfirmPopup = ({ title, message, onYes, onCancel }) => (
  <Modal>
    <div className="px-6 py-5">
      <div className="flex items-center gap-3 mb-3">
        <i className="fas fa-question-circle text-indigo-500 text-2xl" />
        <h3 className="font-bold text-gray-800 text-lg">{title}</h3>
      </div>
      <p className="text-gray-600 text-sm mb-6">{message}</p>
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-5 py-2 border border-gray-300 rounded-full text-gray-600 hover:bg-gray-50 text-sm"
        >
          Cancel
        </button>
        <button
          onClick={onYes}
          className="px-5 py-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 text-sm font-semibold"
        >
          Yes
        </button>
      </div>
    </div>
  </Modal>
);

// ─── Add Product popup ────────────────────────────────────────────────────────
const AddProductPopup = ({
  initialName,
  initialProductType,
  initialManufacturer,
  onSave,
  onClose,
  loading,
  categories = [],
  fieldConfig = {},
}) => {
  // categories expected to have { code, type } shape
  const [form, setForm] = useState({
    productName: initialName || "",
    productType: initialProductType || "",
    manufacturer: initialManufacturer || "",
    //size: "",
  });
  const [errors, setErrors] = useState({});
  const [productTypes, setProductTypes] = useState([]);
  useEffect(() => {
    stockService
      .getProductType()
      .then((res) => {
        setProductTypes(res || []);
      })
      .catch(() => setProductTypes([]));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!form.productName.trim()) {
      newErrors.productName = "Product name is required";
    }

    if (fieldConfig.productType?.visible && fieldConfig.productType?.mandatory && !form.productType.trim()) {
      newErrors.productType = "Product type is required";
    }

    {/*if (fieldConfig.size?.mandatory && !form.size.trim()) {
      newErrors.size = "Size is required";
    }*/}

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    //if (!form.productName.trim()) {
    //  setErrors({ productName: "Product name is required" });
    //  return;
    //}
    onSave(form);
  };

  return (
    <Modal>
      <div className="cursor-move">
        {/* Header matching parent section */}
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 rounded-t-xl">
          <div className="flex items-center gap-2">
            <i className="fas fa-pills text-indigo-600" />
            <h3 className="font-bold text-blue-800 text-lg">Product Create</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 transition-colors"
          >
            <i className="fas fa-times text-xl" />
          </button>
        </div>

        {/* Form content */}
        <div className="p-4">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Product Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="productName"
                  value={form.productName}
                  onChange={handleChange}
                  className={`w-52 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${
                    errors.productName
                      ? "border-red-400 bg-red-50"
                      : "border-blue-300"
                  }`}
                  placeholder="Product name...."
                />
                {errors.productName && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.productName}
                  </p>
                )}
              </div>
              {fieldConfig.productType?.visible && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Product Type
                  {fieldConfig.productType?.mandatory && <span className="text-red-500"> *</span>}
                </label>
                <ProductTypeCombobox
                  value={form.productType}
                  options={productTypes}
                  onChange={(val) => {
                    setForm((prev) => ({ ...prev, productType: val }));
                    if (errors.productType) {
                      setErrors((prev) => ({ ...prev, productType: "" }));
                    }
                  }}
                />
                {errors.productType && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.productType}
                  </p>
                )}
              </div>
              )}

              {/*{fieldConfig.size?.visible && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Size
                    {fieldConfig.size?.mandatory && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>

                  <input
                    type="text"
                    name="size"
                    value={form.size}
                    onChange={handleChange}
                    className={`w-52 px-3 py-2 border rounded-lg text-sm ${
                      errors.size
                        ? "border-red-400 bg-red-50"
                        : "border-blue-300"
                    }`}
                    placeholder="size"
                  />

                  {errors.size && (
                    <p className="text-red-500 text-xs mt-1">{errors.size}</p>
                  )}
                </div>
              )}*/}
              {fieldConfig.manufacturer?.visible && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Manufacturer
                  {fieldConfig.manufacturer?.mandatory && <span className="text-red-500"> *</span>}
                </label>
                <input
                  type="text"
                  name="manufacturer"
                  value={form.manufacturer}
                  onChange={handleChange}
                  className="w-52 px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                  placeholder="e.g. GSK"
                />
              </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold text-xs transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-white font-semibold shadow text-xs transition ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"
                }`}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save" />
                    Save Product
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

// ─── Empty stock row ──────────────────────────────────────────────────────────
const emptyStockRow = (
  productId,
  productName,
  medicineType = "",
  //size = "",
) => ({
  _id: Date.now() + Math.random(),
  productId,
  productName,
  medicineType,
  barcode: "",
  newBarcode: false,
  batchNo: "",
  boxNo: "",
  size: "",
  hsn: "",
  gst: "",
  quantity: "",
  stripCount: "NA",
  qtyPerStrip: "",
  purchasePrice: "",
  purchaseTotal: "",
  mrp: "",
  salesDiscount: "",
  sellPrice: "",
  purchaseDate: "",
  expiryDate: "",
  //size: "",
  errors: {},
});

// ─── Category Combobox ───────────────────────────────────────────────────────
const CategoryCombobox = ({ value, categories, onChange }) => {
  const [input, setInput] = useState(value || "");
  const [open, setOpen] = useState(false);

  const filtered = input
    ? categories.filter((c) =>
        c.type.toLowerCase().includes(input.toLowerCase()),
      )
    : categories;

  const select = (c) => {
    setInput(c.type);
    onChange(c.type);
    setOpen(false);
  };

  const handleBlur = () => {
    setTimeout(() => setOpen(false), 150);
    onChange(input);
  };

  return (
    <div className="relative w-28">
      <input
        type="text"
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        placeholder="Category"
        autoComplete="off"
        className="w-full pl-2 pr-6 py-1 border border-blue-200 rounded text-xs focus:ring-1 focus:ring-indigo-400 focus:outline-none bg-white"
      />
      <span
        onMouseDown={(e) => {
          e.preventDefault();
          setOpen((o) => !o);
        }}
        className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-auto cursor-pointer"
      >
        <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
          <path
            d="M1 3l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      </span>
      {open && filtered.length > 0 && (
        <ul className="absolute z-50 left-0 mt-1 bg-white border border-blue-200 rounded shadow-lg max-h-36 overflow-y-auto text-xs min-w-max">
          {filtered.map((c) => (
            <li
              key={c.code}
              onMouseDown={() => select(c)}
              className="px-3 py-1.5 cursor-pointer hover:bg-blue-50 text-gray-700"
            >
              {c.type}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ─── Stock row component ──────────────────────────────────────────────────────
const StockRow = ({
  row,
  onChange,
  onRemove,
  onDuplicate,
  categories = [],
  fieldConfig = {},
}) => {
  const isNA = !row.stripCount || row.stripCount === "NA";

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...row, [name]: value };

    if (name === "stripCount") {
      if (value === "NA" || value === "") {
        updated.stripCount = "NA";
        updated.qtyPerStrip = "0";
      } else {
        updated.stripCount = value.replace(/[^0-9]/g, "");
        const strips = parseFloat(updated.stripCount) || 0;
        const perStrip = parseFloat(updated.qtyPerStrip) || 0;
        updated.quantity = String(
          strips > 0 && perStrip > 0 ? strips * perStrip : 0,
        );
      }
    }

    if (name === "qtyPerStrip") {
      const strips = parseFloat(updated.stripCount) || 0;
      const perStrip = parseFloat(value) || 0;
      updated.quantity = String(
        strips > 0 && perStrip > 0 ? strips * perStrip : 0,
      );
    }

    // Bi-directional MRP <-> Total MRP
    const qty = parseFloat(updated.quantity) || 0;
    if (name === "mrp") {
      const unitMrp = parseFloat(updated.mrp) || 0;
      updated.sellPrice = qty > 0 && unitMrp > 0 ? (unitMrp * qty).toFixed(2) : "";
    } else {
      const totalMrp = parseFloat(updated.sellPrice) || 0;
      updated.mrp = qty > 0 && totalMrp > 0 ? (totalMrp / qty).toFixed(2) : "";
    }

    // Bi-directional Purchase <-> Purchase Total
    if (name === "purchasePrice") {
      const unitPurchase = parseFloat(updated.purchasePrice) || 0;
      updated.purchaseTotal = qty > 0 && unitPurchase > 0 ? (unitPurchase * qty).toFixed(2) : "";
    } else {
      const purchaseTotal = parseFloat(updated.purchaseTotal) || 0;
      updated.purchasePrice = qty > 0 && purchaseTotal > 0 ? (purchaseTotal / qty).toFixed(2) : "";
    }

    if (updated.errors?.[name])
      updated.errors = { ...updated.errors, [name]: "" };
    onChange(row._id, updated);
  };

  const e = row.errors || {};
  const inputCls = (err) =>
    `px-2 py-1 border rounded text-xs focus:ring-1 focus:ring-indigo-400 focus:outline-none ${err ? "border-red-400" : "border-blue-200"}`;
  const readonlyCls =
    "px-2 py-1 border border-gray-200 rounded text-xs bg-gray-100 cursor-not-allowed text-gray-500";

  return (
    <tr className="border-t hover:bg-indigo-50 transition-colors">
      {/* Product */}
      <td className="py-2 px-3">
        <span className="text-sm font-medium text-indigo-700">
          {row.productName}
        </span>
        {row.medicineType && (
          <span className="text-xs text-gray-400 ml-2">
            ({row.medicineType})
          </span>
        )}
      </td>

      {/* Box No */}
      {fieldConfig.boxNo?.visible && (
        <td className="py-2 px-2">
          <input
            type="text"
            name="boxNo"
            value={row.boxNo}
            onChange={handleChange}
            placeholder="Box No"
            className={`w-20 ${inputCls(e.boxNo)}`}
          />
          {e.boxNo && <p className="text-red-500 text-xs">{e.boxNo}</p>}
        </td>
      )}
      {/* Batch No */}
      <td className="py-2 px-2">
        <input
          type="text"
          name="batchNo"
          value={row.batchNo}
          onChange={handleChange}
          placeholder="BATCH-001"
          className={`w-24 ${inputCls(false)}`}
        />
      </td>
      {/* Barcode - supports scanner input */}
      <td className="py-2 px-2">
        <input
          type="text"
          name="barcode"
          value={row.barcode}
          onChange={handleChange}
          onKeyDown={(ev) => { if (ev.key === "Enter") { ev.preventDefault(); ev.target.blur(); } }}
          disabled={!!row.newBarcode}
          placeholder="Scan / Type"
          className={`w-28 ${inputCls(false)}`}
        />
      </td>
      {/* New Barcode checkbox */}
      {fieldConfig.newBarcode?.visible && (
        <td className="py-2 px-2 text-center">
          <input
            type="checkbox"
            name="newBarcode"
            checked={!!row.newBarcode}
            disabled={!!row.barcode?.trim()}
            onChange={(ev) => {
              const updated = { ...row, newBarcode: ev.target.checked };
              onChange(row._id, updated);
            }}
            className={`w-4 h-4 cursor-pointer ${row.barcode?.trim() ? "opacity-50 cursor-not-allowed" : ""}`}
          />
        </td>
      )}
      {fieldConfig.size?.visible && (
      <td className="py-2 px-2">
        <input
          type="text"
          name="size"
          value={row.size}
          onChange={handleChange}
          placeholder="size.."
          className={`w-24 ${inputCls(false)}`}
        />
      </td>)}
      {/* HSN */}
      <td className="py-2 px-2">
        <input
          type="text"
          name="hsn"
          value={row.hsn}
          onChange={handleChange}
          placeholder="HSN"
          className={`w-24 ${inputCls(false)}`}
        />
      </td>
      {/* Strips — dropdown with NA + numbers, user can also type digits */}
      {fieldConfig.stripCount?.visible && (
        <td className="py-2 px-2">
          <div className="relative w-16">
            <input
              type="text"
              value={row.stripCount || "NA"}
              onChange={(ev) => {
                const v = ev.target.value;
                if (v === "" || v.toUpperCase() === "NA") {
                  handleChange({ target: { name: "stripCount", value: "NA" } });
                } else {
                  const digits = v.replace(/[^0-9]/g, "");
                  handleChange({
                    target: { name: "stripCount", value: digits || "NA" },
                  });
                }
              }}
              onFocus={(ev) => {
                if (ev.target.value === "NA") ev.target.select();
              }}
              className={`w-full text-center pr-5 ${inputCls(e.stripCount)}`}
            />
            <select
              value={row.stripCount || "NA"}
              onChange={(e) =>
                handleChange({
                  target: { name: "stripCount", value: e.target.value },
                })
              }
              size={1}
              className="absolute right-0 top-0 h-full w-5 opacity-0 cursor-pointer"
              style={{ fontSize: "12px" }}
            >
              <option value="NA">NA</option>
              {[...Array(100)].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </select>
          </div>
        </td>
      )}

      {/* Quantity/Strip — disabled if NA */}
      {fieldConfig.stripCount?.visible && (
        <td className="py-2 px-2">
          {isNA ? (
            <span className={`w-16 block text-center ${readonlyCls}`}>0</span>
          ) : (
            <input
              type="number"
              name="qtyPerStrip"
              value={row.qtyPerStrip}
              onChange={handleChange}
              min="0"
              className={`w-16 ${inputCls(e.qtyPerStrip)}`}
            />
          )}
        </td>
      )}
      {/* Quantity — enabled if NA, disabled if numeric strip */}
      <td className="py-2 px-2">
        {isNA ? (
          <>
            <input
              type="number"
              name="quantity"
              value={row.quantity}
              onChange={handleChange}
              min="0"
              className={`w-20 ${inputCls(e.quantity)}`}
            />
            {e.quantity && <p className="text-red-500 text-xs">{e.quantity}</p>}
          </>
        ) : (
          <span className={`w-20 block text-center ${readonlyCls}`}>
            {row.quantity}
          </span>
        )}
      </td>

      {/* MRP — editable, bi-directional with Total MRP */}
      <td className="py-2 px-2">
        <input
          type="number"
          name="mrp"
          value={row.mrp}
          onChange={handleChange}
          min="0"
          step="0.01"
          placeholder="0.00"
          className={`w-20 ${inputCls(e.mrp)}`}
        />
        {e.mrp && <p className="text-red-500 text-xs">{e.mrp}</p>}
      </td>

      {/* Total MRP — always editable (manual entry) */}
      <td className="py-2 px-2">
        <input
          type="number"
          name="sellPrice"
          value={row.sellPrice}
          onChange={handleChange}
          min="0"
          step="0.01"
          className={`w-24 ${inputCls(e.mrp)}`}
        />
        {e.sellPrice && <p className="text-red-500 text-xs">{e.sellPrice}</p>}
      </td>

      {/* Purchase ₹ — editable, bi-directional with Purchase Total */}
      <td className="py-2 px-2">
        <input
          type="number"
          name="purchasePrice"
          value={row.purchasePrice}
          onChange={handleChange}
          min="0"
          step="0.01"
          placeholder="0.00"
          className={`w-20 ${inputCls(e.purchasePrice)}`}
        />
      </td>

      {/* Purchase Total — manual entry */}
      <td className="py-2 px-2">
        <input
          type="number"
          name="purchaseTotal"
          value={row.purchaseTotal || ""}
          onChange={handleChange}
          min="0"
          step="0.01"
          placeholder="0.00"
          className={`w-20 ${inputCls(e.purchasePrice)}`}
        />
        {e.purchaseTotal && (
          <p className="text-red-500 text-xs">{e.purchaseTotal}</p>
        )}
      </td>
      {/* GST % */}
      <td className="py-2 px-2">
        <select
          name="gst"
          value={row.gst}
          onChange={handleChange}
          className={`w-16 px-1 py-1 border rounded text-xs focus:ring-1 focus:ring-indigo-400 focus:outline-none ${e.salesDiscount ? "border-red-400" : "border-blue-200"}`}
        >
          <option value="">0%</option>
          {[...Array(100)].map((_, i) => (
            <option key={i + 1} value={i + 1}>
              {i + 1}%
            </option>
          ))}
        </select>
      </td>
      {/* Discount % */}
      <td className="py-2 px-2">
        <input
          type="number"
          name="salesDiscount"
          value={row.salesDiscount}
          onChange={handleChange}
          min="0"
          max="100"
          step="0.01"
          placeholder="0"
          className={`w-16 ${inputCls(e.salesDiscount)}`}
        />
      </td>

      {/* Expiry Date */}
      {fieldConfig.expiryDate?.visible && (
        <td className="py-2 px-2">
          <input
            type="date"
            name="expiryDate"
            value={row.expiryDate}
            onChange={handleChange}
            className={`w-32 ${inputCls(e.expiryDate)}`}
          />
          {e.expiryDate && (
            <p className="text-red-500 text-xs">{e.expiryDate}</p>
          )}
        </td>
      )}
      {/* Purchase Date */}
      <td className="py-2 px-2">
        <input
          type="date"
          name="purchaseDate"
          value={row.purchaseDate}
          onChange={handleChange}
          className={`w-32 ${inputCls(e.purchaseDate)}`}
        />
      </td>

      {/* Actions */}
      <td className="py-2 px-3 text-center">
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => onDuplicate(row)}
            title="Add another row for same product"
            className="w-5 h-5 flex items-center justify-center rounded-full border border-blue-400 text-blue-500 hover:bg-blue-500 hover:text-white transition-colors"
          >
            <i className="fas fa-plus text-[9px]" />
          </button>
          <button
            onClick={() => onRemove(row._id)}
            title="Remove row"
            className="text-red-400 hover:text-red-600 transition-colors"
          >
            <i className="fas fa-trash-alt text-xs" />
          </button>
        </div>
      </td>
    </tr>
  );
};
// ─── Product Type Combobox ─────────────────────────────────
const ProductTypeCombobox = ({ value, options = [], onChange }) => {
  const [input, setInput] = useState(value || "");
  const [open, setOpen] = useState(false);

  const filtered = input
    ? options.filter((opt) => opt.toLowerCase().includes(input.toLowerCase()))
    : options;

  const select = (val) => {
    setInput(val);
    onChange(val);
    setOpen(false);
  };

  const handleBlur = () => {
    setTimeout(() => setOpen(false), 150);
    onChange(input);
  };

  return (
    <div className="relative w-52">
      <input
        type="text"
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        placeholder="Select or type"
        className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
      />

      <span
        onMouseDown={(e) => {
          e.preventDefault();
          setOpen((o) => !o);
        }}
        className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400"
      >
        ▼
      </span>

      {open && filtered.length > 0 && (
        <ul className="absolute z-50 left-0 mt-1 w-full bg-white border border-blue-200 rounded shadow max-h-40 overflow-y-auto text-sm">
          {filtered.map((opt, i) => (
            <li
              key={i}
              onMouseDown={() => select(opt)}
              className="px-3 py-2 hover:bg-blue-50 cursor-pointer"
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
// ─── Main Page ────────────────────────────────────────────────────────────────
const StockCreate = () => {
  const [productErrors, setProductErrors] = useState({});
  const [productForm, setProductForm] = useState({
    productName: "",
    productType: "",
    manufacturer: "",
  });
  const [productLookupLoading, setProductLookupLoading] = useState(false);
  const [stockRows, setStockRows] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const submitInProgressRef = useRef(false);
  const { showLoader, hideLoader } = useLoader();
  const [pageMessage, setPageMessage] = useState({ type: "", text: "" });
  const [toast, setToast] = useState(null);
  const [categories, setCategories] = useState([]);
  const [fieldConfig, setFieldConfig] = useState({});
  const isManufacturerMandatory = !!fieldConfig?.manufacturer?.mandatory;
  const isProductTypeMandatory = !!fieldConfig?.productType?.mandatory;
  const productHelpText = isProductTypeMandatory && isManufacturerMandatory
    ? "Please enter product name, product type and manufacturer and tabout"
    : isProductTypeMandatory
    ? "Please enter product name and product type and tabout"
    : "Please enter product name and tabout";

  useEffect(() => {
    stockService
      .getAllCategories()
      .then(setCategories)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const data = await stockService.getClientFieldConfig();

        const map = {};
        (data || []).forEach((f) => {
          if (f.fieldName) {
            map[f.fieldName] = f;
          }
        });

        setFieldConfig(map);
      } catch (err) {
        console.error("CONFIG LOAD FAILED:", err);

        // fallback
        setFieldConfig({
         // size: { visible: true, mandatory: false },
          stripCount: { visible: true },
          expiryDate: { visible: true },
        });
      }
    };

    loadConfig();
  }, []);

  // Distributor states
  const [distributorBillNo, setDistributorBillNo] = useState("");
  const [distributorMobileNo, setDistributorMobileNo] = useState("");
  const [distributorBillSaved, setDistributorBillSaved] = useState(false);
  const [distributorBillId, setDistributorBillId] = useState(null);
  const [distributorId, setDistributorId] = useState(null);
  const distributorBillIdRef = useRef(null);
  const distributorIdRef = useRef(null);
  const distributorSupplyTypeRef = useRef("INTRA");
  const [showDistributorPopup, setShowDistributorPopup] = useState(false);
  const [distributorBillError, setDistributorBillError] = useState("");

  // Popup states
  const [addProductPopup, setAddProductPopup] = useState(false);
  // AI bulk-upload popup (additive — only pre-fills existing state).
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [addProductLoading, setAddProductLoading] = useState(false);
  const [confirmPopup, setConfirmPopup] = useState(null);

  const productInputRef = useRef(null);

  const getErrMsg = (err, fallback) =>
    err?.response?.data?.errorMessage ||
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    (typeof err?.response?.data === "string" ? err.response.data : null) ||
    fallback;

  const showPageMsg = (type, text, ms = 5000) => {
    if (type === "success") {
      const identifier = distributorBillNo.trim()
        ? `Bill No: ${distributorBillNo.trim()}`
        : null;
      setToast({
        type: "success",
        identifier,
        message: text.replace(/^✅\s*/, ""),
      });
      return;
    }
    setToast({
      type: "error",
      identifier: null,
      message: text.replace(/^[⚠️❌]\s*/, ""),
    });
  };

  // ─── Handle distributor mobile blur ─────────────────────────────────
  const handleDistributorFieldBlur = () => {
    if (distributorBillSaved) return;
    // Clear previous error
    setDistributorBillError("");

    // Check if both fields are empty
    if (!distributorBillNo.trim() && !distributorMobileNo.trim()) {
      return;
    }

    // Validate bill number
    if (!distributorBillNo.trim()) {
      setDistributorBillError("Please enter Distributor Bill No");
      return;
    }

    // Validate mobile number
    if (!distributorMobileNo.trim()) {
      setDistributorBillError("Please enter Distributor Mobile No");
      return;
    }

    if (!/^\d{10}$/.test(distributorMobileNo.trim())) {
      setDistributorBillError("Mobile number must be 10 digits");
      return;
    }

    // Both fields are valid, open popup
    setShowDistributorPopup(true);
  };

  // ─── Add a stock row for a confirmed product ────────────────────────────────
  const addStockRow = (productId, productName, medicineType = "") => {
    setStockRows((prev) => [
      ...prev,
      emptyStockRow(productId, productName, medicineType),
    ]);
    showPageMsg(
      "success",
      `✅ Stock row added for "${productName}". Fill in the details below.`,
    );
  };

  // ─── AI bulk-upload: turn an extracted draft into stock rows ──────────────
  // This ONLY pre-fills the same state manual entry uses. It resolves each
  // product through the EXISTING findProductByName/createProduct logic (so the
  // "exists → reuse / not found → create" rule is identical to manual entry),
  // builds rows via the EXISTING emptyStockRow, and fills the distributor bill
  // fields. The user then reviews/edits and saves via the UNCHANGED Save flow.
  const applyAiDraft = async (draft) => {
    const rows = Array.isArray(draft?.rows) ? draft.rows : [];
    if (rows.length === 0) {
      setBulkModalOpen(false);
      return;
    }
    setBulkApplying(true);
    const resolvedByName = new Map();
    const newRows = [];
    let created = 0;
    let failed = 0;

    const resolveProduct = async (name, type, manufacturer) => {
      const key = (name || "").trim().toLowerCase();
      if (!key) return null;
      if (resolvedByName.has(key)) return resolvedByName.get(key);
      let product = null;
      try {
        const found = await stockService.findProductByName(
          name.trim(), (type || "").trim(), (manufacturer || "").trim(),
        );
        if (found && Object.keys(found).length > 0 && found.productId) {
          product = {
            productId: found.productId,
            productName: found.productName,
            medicineType: found.medicineType || type || "",
          };
        }
      } catch {
        product = null;
      }
      if (!product) {
        const saved = await stockService.createProduct({
          productName: name.trim(),
          medicineType: (type || "").trim() || null,
          manufacturer: (manufacturer || "").trim() || null,
          createdBy: localStorage.getItem("userName") || "system",
        });
        product = {
          productId: saved.productId,
          productName: saved.productName || name.trim(),
          medicineType: saved.medicineType || type || "",
        };
        created += 1;
      }
      resolvedByName.set(key, product);
      return product;
    };

    for (const r of rows) {
      if (!r || !r.productName || !String(r.productName).trim()) {
        failed += 1;
        continue;
      }
      let product;
      try {
        product = await resolveProduct(r.productName, r.productType, r.manufacturer);
      } catch {
        failed += 1;
        continue;
      }
      if (!product) {
        failed += 1;
        continue;
      }
      const row = emptyStockRow(product.productId, product.productName, product.medicineType);
      const num = (v) => (v === null || v === undefined || v === "" ? "" : String(v));
      row.batchNo = num(r.batchNo);
      row.hsn = num(r.hsn);
      row.gst = num(r.gst);
      row.quantity = num(r.quantity);
      row.mrp = num(r.mrp);
      row.purchasePrice = num(r.purchasePrice);
      row.salesDiscount = num(r.salesDiscount);
      row.size = num(r.size);
      row.expiryDate = num(r.expiryDate);
      const qty = parseFloat(row.quantity);
      const mrp = parseFloat(row.mrp);
      const pp = parseFloat(row.purchasePrice);
      if (!Number.isNaN(qty) && !Number.isNaN(mrp)) row.sellPrice = String(mrp * qty);
      if (!Number.isNaN(qty) && !Number.isNaN(pp)) row.purchaseTotal = String(pp * qty);
      newRows.push(row);
    }

    if (newRows.length > 0) {
      setStockRows((prev) => [...prev, ...newRows]);
    }
    const dist = draft?.distributor || {};
    if (dist.billNo) setDistributorBillNo(String(dist.billNo));
    if (dist.mobileNo && /^\d{10}$/.test(String(dist.mobileNo))) {
      setDistributorMobileNo(String(dist.mobileNo));
    }

    setBulkApplying(false);
    setBulkModalOpen(false);
    showPageMsg(
      newRows.length > 0 ? "success" : "error",
      `Added ${newRows.length} row(s)` +
        (created ? `, created ${created} new product(s)` : "") +
        (failed ? `. Skipped ${failed} unreadable item(s).` : ".") +
        " Review the details and link the distributor bill, then Save.",
    );
  };

  // If Jash (the chat assistant) handed off an extracted bill, pre-fill the
  // page with it once on mount — reusing the SAME applyAiDraft logic as the
  // in-page popup. The user still reviews and saves via the unchanged flow.
  const chatDraftHandled = useRef(false);
  useEffect(() => {
    if (chatDraftHandled.current) return;
    let raw = null;
    try {
      raw = sessionStorage.getItem("jashStockDraft");
    } catch {
      raw = null;
    }
    if (!raw) return;
    chatDraftHandled.current = true;
    try {
      sessionStorage.removeItem("jashStockDraft");
    } catch {
      /* ignore */
    }
    try {
      const draft = JSON.parse(raw);
      if (draft && Array.isArray(draft.rows) && draft.rows.length > 0) {
        applyAiDraft(draft);
      }
    } catch {
      /* ignore malformed handoff */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProductNameChange = (value) => {
    setProductForm((prev) => ({ ...prev, productName: value }));
  };

  // ─── Handle product name blur (tab out) ────────────────────────────────────
  const tryOpenProductFlow = async () => {
    console.log("🔥 tryOpenProductFlow triggered");
    const name = productForm.productName.trim();
    const manufacturer = productForm.manufacturer.trim();

    let errors = {};

    if (!name) {
      console.log("❌ Name missing");
      errors.productName = "Product name is required";
    }

    if (fieldConfig.manufacturer?.visible && fieldConfig.manufacturer?.mandatory && !manufacturer) {
      errors.manufacturer = "Manufacturer is required";
    }

    if (Object.keys(errors).length > 0) {
      setProductErrors(errors);
      return;
    }

    setProductErrors({});

    setProductLookupLoading(true);

    try {
      console.log("📡 Calling API...");
      const found = await stockService.findProductByName(name, productForm.productType?.trim() || "", productForm.manufacturer?.trim() || "");
      console.log("✅ FOUND RESPONSE:", found);
      if (found && Object.keys(found).length > 0 && found.productId) {
         console.log("👉 EXISTING PRODUCT FLOW..........");
        setProductForm({
          productName: found.productName,
          productType: found.medicineType || "",
          manufacturer: found.manufacturer || manufacturer,
        });

        setConfirmPopup({
          
          title: "Product Already Exists",
          message: `"${found.productName}" is already in the system. Do you want to add a new stock batch for it?`,
          onYes: () => {
            setConfirmPopup(null);
            addStockRow(
              found.productId,
              found.productName,
              found.medicineType || "",
            );
          },
          onCancel: () => setConfirmPopup(null),
        });
      } else {
        console.log("👉 NEW PRODUCT FLOW");
        // ❌ Only when truly NOT FOUND
        setAddProductPopup(true);
      }
    } catch {
      setAddProductPopup(true);
    } finally {
      setProductLookupLoading(false);
    }
  };

  // ─── Save new product from popup ───────────────────────────────────────────
  const handleSaveNewProduct = async (form) => {
    setAddProductLoading(true);
    try {
      const saved = await stockService.createProduct({
        productName: form.productName.trim(),
        medicineType: form.productType || null,
        manufacturer: form.manufacturer.trim() || null,
        //size: form.size.trim() || null,
        createdBy: localStorage.getItem("userName") || "system",
      });

      setAddProductPopup(false);

      // Populate product details in Product Search section
      setProductForm({
        productName: saved.productName || form.productName.trim(),
        productType: saved.productType || form.productType || "",
        manufacturer: saved.manufacturer || form.manufacturer.trim() || "",
      });

      // Ask user if they want to create stock now
      setConfirmPopup({
        title: "Product Saved!",
        message: `Do you want to create stock for submitted product?`,
        onYes: () => {
          setConfirmPopup(null);
          addStockRow(
            saved.productId,
            saved.productName || form.productName.trim(),
            saved.productType || form.productType || "",
          );
        },
        onCancel: () => {
          setConfirmPopup(null);
          showPageMsg(
            "success",
            `✅ Product "${saved.productName || form.productName}" saved successfully.`,
          );
        },
      });
    } catch (err) {
      const msg = getErrMsg(err, "Failed to save product");
      showPageMsg("error", msg);
      setAddProductPopup(false);
    } finally {
      setAddProductLoading(false);
    }
  };

  // ─── Update a stock row field ───────────────────────────────────────────────
  const handleRowChange = (rowId, updatedRow) => {
    setStockRows((prev) => prev.map((r) => (r._id === rowId ? updatedRow : r)));
  };

  // ─── Remove a stock row ─────────────────────────────────────────────────────
  const handleDuplicateRow = (row) => {
    setStockRows((prev) => [
      ...prev,
      { ...emptyStockRow(row.productId, row.productName, row.medicineType), _id: Date.now() + Math.random() },
    ]);
  };

  const handleRemoveRow = (rowId) => {
    setStockRows((prev) => prev.filter((r) => r._id !== rowId));
  };

  // ─── Validate all rows ──────────────────────────────────────────────────────
  const validateRows = () => {
    let valid = true;

    const updated = stockRows.map((row) => {
      const errors = {};
      // ✅ Strip validation
      const isNA = !row.stripCount || row.stripCount === "NA";

      if (fieldConfig.stripCount?.mandatory) {
        if (!row.stripCount) {
          errors.stripCount = "Required";
        }

        // ✅ ONLY validate qtyPerStrip when stripCount is numeric
        if (!isNA) {
          if (!row.qtyPerStrip || parseInt(row.qtyPerStrip) <= 0) {
            errors.qtyPerStrip = "Required";
          }
        }
      }

      // ✅ Quantity validation (only if NA mode)
      if (isNA) {
        if (!row.quantity || parseInt(row.quantity) <= 0) {
          errors.quantity = "Required";
        }
      }

      // ✅ MRP validation
      if (!row.mrp || parseFloat(row.mrp) <= 0) {
        errors.mrp = "Required";
      }

      // ✅ Total MRP validation
      if (!row.sellPrice || parseFloat(row.sellPrice) <= 0) {
        errors.sellPrice = "Required";
      }

      // ✅ Purchase Price validation
      if (!row.purchasePrice || parseFloat(row.purchasePrice) <= 0) {
        errors.purchasePrice = "Required";
      }

      // ✅ Purchase Total validation
      if (!row.purchaseTotal || parseFloat(row.purchaseTotal) <= 0) {
        errors.purchaseTotal = "Required";
      }

      // ✅ Discount validation
      if (!row.salesDiscount && row.salesDiscount !== "0") {
        errors.salesDiscount = "Required";
      }

      // ✅ Purchase Date validation
      if (!row.purchaseDate) {
        errors.purchaseDate = "Required";
      }

      // ✅ Box No
      if (fieldConfig.boxNo?.visible && fieldConfig.boxNo?.mandatory && !row.boxNo?.trim()) {
        errors.boxNo = "Required";
      }

      // ✅ New Barcode
      if (fieldConfig.newBarcode?.visible && fieldConfig.newBarcode?.mandatory && !row.barcode?.trim() && !row.newBarcode) {
        errors.newBarcode = "Required";
      }

      // ✅ Expiry
      if (fieldConfig.expiryDate?.mandatory && !row.expiryDate) {
        //if (!row.expiryDate) {
        errors.expiryDate = "Required";
        //}
      }

      if (Object.keys(errors).length) valid = false;

      return { ...row, errors };
    });

    setStockRows(updated);
    return valid;
  };

  // ─── Submit all stock rows ──────────────────────────────────────────────────
  const handleSubmitAll = async () => {
    if (submitInProgressRef.current) return; // prevent double-click
    if (stockRows.length === 0) {
      showPageMsg("error", "Add at least one stock row before saving.");
      return;
    }
    if (
      !distributorBillSaved ||
      !distributorBillIdRef.current ||
      !distributorIdRef.current
    ) {
      setDistributorBillError(
        "Distributor bill no and mobile no are required before saving stock.",
      );
      showPageMsg(
        "error",
        "Please link a distributor bill before saving stock.",
      );
      return;
    }
    if (!validateRows()) {
      showPageMsg("error", "Please fix the highlighted errors before saving.");
      return;
    }

    submitInProgressRef.current = true;
    setSubmitLoading(true);
    showLoader();
    const actionBy = localStorage.getItem("userName") || "system";
    let successCount = 0;
    let failCount = 0;
    const savedRows = [];

    for (const row of stockRows) {
      try {
        await stockService.createStock({
          productId: row.productId,
          barcode: row.barcode?.trim() || null,
          newBarcode: !!row.newBarcode,
          batchNo: row.batchNo.trim() || null,
          boxNo: row.boxNo?.trim() || null,
          size: row.size ? parseFloat(row.size) : 0,
          hsn: row.hsn.trim() || null,
          gst: row.gst ? parseFloat(row.gst) : 0,
          quantity: parseInt(row.quantity),
          stripCount: row.stripCount ? parseInt(row.stripCount) : null,
          qtyPerStrip: row.qtyPerStrip ? parseInt(row.qtyPerStrip) : null,
          purchasePrice: parseFloat(row.purchasePrice),
          purchaseTotalPrice: parseFloat(row.purchaseTotal) || null,
          mrp: parseFloat(row.mrp) || null,
          totalMrp: parseFloat(row.sellPrice) || null,
          salesDiscount: row.salesDiscount ? parseFloat(row.salesDiscount) : 0,
          purchaseDate: row.purchaseDate || null,
          expiryDate: row.expiryDate,
          actionBy,
          distributorBillId: distributorBillIdRef.current,
          distributorId: distributorIdRef.current,
        });
        savedRows.push(row);
        successCount++;
      } catch (err) {
        showPageMsg(
          "error",
          getErrMsg(err, `Failed to save stock for "${row.productName}"`),
        );
        failCount++;
      }
    }

    if (successCount > 0) {
      // Snapshot GST items only for successfully saved rows
      try {
        const supplyType = distributorSupplyTypeRef.current || "INTRA";
        const billItems = savedRows.map((row) => {
          const gstRate = parseFloat(row.gst) || 0;
          const purchaseTotal = parseFloat(row.purchaseTotal) || 0;
          const taxableAmount = gstRate > 0
            ? parseFloat((purchaseTotal / (1 + gstRate / 100)).toFixed(2))
            : purchaseTotal;
          const totalGst = parseFloat((purchaseTotal - taxableAmount).toFixed(2));
          const cgst = supplyType === "INTRA" ? parseFloat((totalGst / 2).toFixed(2)) : 0;
          const sgst = supplyType === "INTRA" ? parseFloat((totalGst / 2).toFixed(2)) : 0;
          const igst = supplyType === "INTER" ? totalGst : 0;
          return {
            productName: row.productName,
            hsnCode: row.hsn?.trim() || null,
            gstRate,
            unitPrice: parseFloat(row.purchasePrice),
            quantity: parseInt(row.quantity),
            taxableAmount,
            cgstAmount: cgst,
            sgstAmount: sgst,
            igstAmount: igst,
            totalGst,
            itemTotal: purchaseTotal,
          };
        });
        await auditService.updateBillWithItems(distributorBillIdRef.current, billItems);
        // Remove only the successfully saved rows
        setStockRows((prev) => prev.filter((r) => !savedRows.some((s) => s._id === r._id)));
        if (failCount === 0) {
          showPageMsg("success", `All ${successCount} stock batch(es) saved successfully!`);
        } else {
          showPageMsg("error", `${successCount} saved, ${failCount} failed. Fix the remaining rows and retry.`);
        }
      } catch (err) {
        showPageMsg("error", getErrMsg(err, "Stock saved but GST snapshot failed. Please retry saving."));
      }
    } else {
      showPageMsg("error", `All ${failCount} row(s) failed. Check the rows and retry.`);
    }

    setSubmitLoading(false);
    hideLoader();
    submitInProgressRef.current = false;
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="h-full flex flex-col bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-4 overflow-auto">
      <Header showSearch={false} showCart={false} />

      {/* Popups */}
      {addProductPopup && (
        <AddProductPopup
          fieldConfig={fieldConfig}
          initialName={productForm.productName}
          initialProductType={productForm.productType}
          initialManufacturer={productForm.manufacturer}
          onSave={handleSaveNewProduct}
          onClose={() => setAddProductPopup(false)}
          loading={addProductLoading}
          categories={categories}
        />
      )}
      {confirmPopup && (
        <ConfirmPopup
          title={confirmPopup.title}
          message={confirmPopup.message}
          onYes={confirmPopup.onYes}
          onCancel={confirmPopup.onCancel}
        />
      )}
      {showDistributorPopup && (
        <DistributorBillPopup
          billNo={distributorBillNo}
          mobileNo={distributorMobileNo}
          isEditMode={distributorBillSaved}
          distributorId={distributorId}
          billId={distributorBillId}
          onSave={(billId, distId, supplyType) => {
            setShowDistributorPopup(false);
            setDistributorBillSaved(true);
            setDistributorBillId(billId);
            setDistributorId(distId);
            distributorBillIdRef.current = billId;
            distributorIdRef.current = distId;
            distributorSupplyTypeRef.current = supplyType || "INTRA";
            showPageMsg(
              "success",
              distributorBillSaved
                ? "✅ Distributor bill updated successfully!"
                : "✅ Distributor bill saved successfully!",
            );
          }}
          onClose={() => setShowDistributorPopup(false)}
          onBillNoChange={(value) => setDistributorBillNo(value)}
          onMobileNoChange={(value) => setDistributorMobileNo(value)}
        />
      )}

      {toast && (
        <Toast
          type={toast.type}
          identifier={toast.identifier}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}

      {bulkModalOpen && (
        <BulkStockUploadModal
          onApply={applyAiDraft}
          onClose={() => setBulkModalOpen(false)}
          applying={bulkApplying}
        />
      )}

      {/* ── SECTION 1: Product Search ── */}
      <section className="relative rounded-xl shadow-md border border-blue-200 mt-4">
        <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 rounded-t-xl">
          <button
            type="button"
            onClick={() => setBulkModalOpen(true)}
            className="order-2 flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 hover:underline"
            title="Upload a bill/sheet and let AI fill the stock rows"
          >
            <i className="fas fa-robot" /> Bulk Upload by AI
          </button>
        <h2 className="order-1 text-left text-sm font-bold text-blue-800">
          💊 Product Search
        </h2>
        </div>
        <div className="p-3">
          <div className="flex flex-wrap gap-2">
            <div className="relative">
              <div className="flex items-center justify-between w-52 mb-1">
                <label className="text-xs font-medium text-gray-600">
                  Product Name <span className="text-red-500">*</span>
                </label>
              </div>
              <input
                type="text"
                value={productErrors.productName ? "" : productForm.productName}
                onChange={(e) => {
                  handleProductNameChange(e.target.value);

                  if (productErrors.productName) {
                    setProductErrors((prev) => ({ ...prev, productName: "" }));
                  }
                }}
                onBlur={tryOpenProductFlow}
                placeholder={
                  productErrors.productName
                    ? "Product name is required"
                    : "Product name...."
                }
                disabled={productLookupLoading}
                className={`w-44 px-3 py-1 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none 
    ${
      productErrors.productName
        ? "border-red-400 bg-red-50 placeholder-red-400"
        : "border-blue-300"
    }`}
              />
              {productLookupLoading && (
                <p className="absolute left-0 top-full text-xs text-indigo-600 mt-1 z-10">
                  <i className="fas fa-spinner fa-spin" /> Searching...
                </p>
              )}
            </div>
            {fieldConfig.productType?.visible && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Product Type
                {fieldConfig.productType?.mandatory && <span className="text-red-500"> *</span>}
              </label>
              <input
                type="text"
                value={productForm.productType}
                onChange={(e) => setProductForm((prev) => ({ ...prev, productType: e.target.value }))}
                onBlur={tryOpenProductFlow}
                placeholder="Product type..."
                className="w-44 px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
              />
            </div>
            )}
            {fieldConfig.manufacturer?.visible && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Manufacturer{" "}
                {fieldConfig.manufacturer?.mandatory && (
                  <span className="text-red-500">*</span>
                )}
              </label>
              <input
                type="text"
                value={
                  productErrors.manufacturer ? "" : productForm.manufacturer
                }
                onChange={(e) => {
                  const value = e.target.value;

                  setProductForm((prev) => ({
                    ...prev,
                    manufacturer: value,
                  }));

                  // remove error when typing starts
                  if (productErrors.manufacturer) {
                    setProductErrors((prev) => ({ ...prev, manufacturer: "" }));
                  }
                }}
                onBlur={tryOpenProductFlow}

                placeholder={
                  productErrors.manufacturer
                    ? "Manufacturer is required"
                    : "manufacturer names..."
                }
                className={`w-44 px-3 py-1 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${productErrors.manufacturer ? "border-red-400 bg-red-50 placeholder-red-400" : "border-gray-300 bg-white"}`}
              />
            </div>
            )}
          </div>
          <p className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-red-500">
            <i className="fas fa-info-circle" />
            {productHelpText}
          </p>
        </div>
      </section>

      {/* ── SECTION 2: Stock Creation Table ── */}
      <section className="mt-4 bg-white rounded-xl shadow-md border border-blue-200 overflow-hidden flex flex-col">
        <div className="px-5 py-3 bg-gradient-to-r from-green-100 via-teal-100 to-blue-100 border-b border-blue-200">
          <div className="flex items-start justify-between gap-3">
            {/* Left: Title and badge */}
            <div className="flex items-center gap-3">
              <i className="fas fa-boxes text-green-600" />
              <h2 className="font-bold text-blue-800">Stock Create</h2>
              {stockRows.length > 0 && (
                <span className="bg-indigo-100 text-indigo-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                  {stockRows.length} row{stockRows.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>

            {/* Right: Distributor fields + hint + Save button */}
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2">
                {distributorBillError && (
                  <p className="text-red-500 text-xs whitespace-nowrap">
                    {distributorBillError}
                  </p>
                )}
                {distributorBillSaved && (
                  <button
                    onClick={() => {
                      setDistributorBillSaved(false);
                      setDistributorBillNo("");
                      setDistributorMobileNo("");
                      setDistributorBillId(null);
                      setDistributorId(null);
                      distributorBillIdRef.current = null;
                      distributorIdRef.current = null;
                      setDistributorBillError("");
                    }}
                    className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600 transition-colors flex items-center gap-1"
                  >
                    <i className="fas fa-redo text-xs" /> Reset
                  </button>
                )}
                <input
                  type="text"
                  value={distributorBillNo}
                  onChange={(e) => {
                    setDistributorBillNo(e.target.value);
                    if (e.target.value.trim()) setDistributorBillError("");
                  }}
                  onBlur={handleDistributorFieldBlur}
                  placeholder="Distributor Bill No"
                  readOnly={distributorBillSaved}
                  className={`w-40 px-3 py-1.5 border rounded-lg text-xs focus:ring-2 focus:ring-indigo-400 focus:outline-none ${distributorBillSaved ? "bg-gray-100 cursor-not-allowed border-blue-300" : distributorBillError === "Please enter Distributor Bill No" ? "border-red-400 bg-red-50" : "border-blue-300 bg-white"}`}
                />
                <input
                  type="tel"
                  value={distributorMobileNo}
                  onChange={(e) => {
                    const value = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10);
                    setDistributorMobileNo(value);
                  }}
                  onBlur={handleDistributorFieldBlur}
                  placeholder="Distributor Mobile No"
                  maxLength="10"
                  readOnly={distributorBillSaved}
                  className={`w-40 px-3 py-1.5 border rounded-lg text-xs focus:ring-2 focus:ring-indigo-400 focus:outline-none ${distributorBillSaved ? "bg-gray-100 cursor-not-allowed border-blue-300" : distributorBillError === "Please enter Distributor Mobile No" || distributorBillError === "Mobile number must be 10 digits" ? "border-red-400 bg-red-50" : "border-blue-300 bg-white"}`}
                />
                {distributorBillSaved && (
                  <div className="relative group">
                    <input
                      type="checkbox"
                      checked={true}
                      onChange={async (e) => {
                        e.preventDefault();
                        if (distributorId && distributorBillId) {
                          try {
                            await distributorService.getDistributorWithBill(
                              distributorId,
                              distributorBillId,
                            );
                            setShowDistributorPopup(true);
                          } catch (err) {
                            showPageMsg(
                              "error",
                              getErrMsg(err, "Failed to fetch bill details"),
                            );
                          }
                        }
                      }}
                      className="w-4 h-4 text-green-600 cursor-pointer"
                      title="Distributor bill saved"
                    />
                    <span className="absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 right-full mr-2 top-1/2 transform -translate-y-1/2 whitespace-nowrap z-50 shadow-lg">
                      Click to edit bill information
                    </span>
                  </div>
                )}
              </div>
              {!distributorBillSaved ? (
                <p className="text-xs text-red-400 italic">
                  <i className="fas fa-exclamation-circle mr-1 text-red-400" />
                  Required: Enter bill no &amp; mobile, tab out to link
                  distributor bill before saving stock.
                </p>
              ) : (
                <p className="text-xs text-green-600 font-medium">
                  <i className="fas fa-check-circle mr-1" />
                  Distributor bill linked. Click checkbox to edit bill details.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto overflow-y-auto max-h-[400px]">
          <table className="min-w-full text-sm">
            <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 text-xs uppercase tracking-wide">
              <tr>
                <th className="py-3 px-3 text-left">Product</th>
                {fieldConfig.boxNo?.visible && (
                  <th className="py-3 px-2 text-left">Box No{fieldConfig.boxNo?.mandatory && <span className="text-red-400"> *</span>}</th>
                )}
                <th className="py-3 px-2 text-left">Batch No</th>
                <th className="py-3 px-2 text-left">Barcode</th>
                {fieldConfig.newBarcode?.visible && (
                  <th className="py-3 px-2 text-center">New Barcode{fieldConfig.newBarcode?.mandatory && <span className="text-red-400"> *</span>}</th>
                )}
                {fieldConfig.size?.visible && (
                <th className="py-3 px-2 text-left">Size
                  {fieldConfig.size?.mandatory && (
                      <span className="text-red-500">*</span>
                    )}
                </th>)}
                <th className="py-3 px-2 text-left">HSN</th>
                {fieldConfig.stripCount?.visible && (
                  <th className="py-3 px-2 text-left">
                    Strips
                    {fieldConfig.stripCount?.mandatory && (
                      <span className="text-red-500">*</span>
                    )}
                  </th>
                )}
                {fieldConfig.stripCount?.visible && (
                  <th className="py-3 px-2 text-left">
                    Quantity/Strip
                    {fieldConfig.stripCount?.mandatory && (
                      <span className="text-red-500">*</span>
                    )}
                  </th>
                )}
                {/*<th className="py-3 px-2 text-left">Quantity/Strip</th>*/}
                <th className="py-3 px-2 text-left">
                  Quantity <span className="text-red-400">*</span>
                </th>
                <th className="py-3 px-2 text-left">
                  MRP ₹ <span className="text-red-400">*</span>
                </th>
                <th className="py-3 px-2 text-left">Total MRP <span className="text-red-400">*</span></th>
                <th className="py-3 px-2 text-left">Purchase ₹ <span className="text-red-400">*</span></th>
                <th className="py-3 px-2 text-left">Purchase Total <span className="text-red-400">*</span></th>
                <th className="py-3 px-2 text-left">GST %</th>
                <th className="py-3 px-2 text-left">Discount % <span className="text-red-400">*</span></th>
                {fieldConfig.expiryDate?.visible && (
                  <th className="py-3 px-2 text-left">
                    Expiry Date <span className="text-red-400">*</span>
                  </th>
                )}
                <th className="py-3 px-2 text-left">Purchase Date <span className="text-red-400">*</span></th>
                <th className="py-3 px-2 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {stockRows.length === 0 ? (
                <tr>
                  <td colSpan="13" className="py-14 text-center text-gray-400">
                    <i className="fas fa-inbox text-4xl mb-3 block" />
                    <p className="text-sm">No stock rows yet.</p>
                    <p className="text-xs mt-1">
                      Search for a product above to add stock batches here.
                    </p>
                  </td>
                </tr>
              ) : (
                stockRows.map((row, idx) => (
                  <StockRow
                    key={row._id}
                    row={row}
                    onChange={handleRowChange}
                    onRemove={handleRemoveRow}
                    onDuplicate={handleDuplicateRow}
                    categories={categories}
                    fieldConfig={fieldConfig}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {stockRows.length > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 flex justify-end sticky bottom-0 bg-white z-10">
            <button
              onClick={handleSubmitAll}
              disabled={submitLoading}
              className={`flex items-center gap-2 px-6 py-2 rounded-full text-white font-semibold text-sm shadow transition ${
                submitLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600"
              }`}
            >
              {submitLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin" /> Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save" /> Save All ({stockRows.length})
                </>
              )}
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default StockCreate;
